import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Badge, Spinner } from 'react-bootstrap';
import { FaCertificate, FaDownload, FaCalendarAlt, FaTrophy, FaGraduationCap } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../api/apiClient';

const MyCertificatesPage = () => {
  const { user } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCertificates, setTotalCertificates] = useState(0);

  useEffect(() => {
    if (user?.id) {
      fetchCertificates();
    }
  }, [user, currentPage]);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const response = await api.certificates.getLearnerCertificates(user.id, {
        page: currentPage,
        limit: 12
      });

      if (response.data.success) {
        setCertificates(response.data.data.certificates);
        setTotalPages(response.data.data.pagination.totalPages);
        setTotalCertificates(response.data.data.pagination.totalItems);
      }
    } catch (error) {
      console.error('Error fetching certificates:', error);
      toast.error('Failed to fetch your certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (certificate) => {
    try {
      setDownloading(prev => ({ ...prev, [certificate.id]: true }));
      
      const response = await api.certificates.downloadCertificate(certificate.id);
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate-${certificate.learner_name.replace(/\s+/g, '-')}-${certificate.course_name.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded successfully');
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
    } finally {
      setDownloading(prev => ({ ...prev, [certificate.id]: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderCertificateCard = (certificate) => (
    <Col key={certificate.id} lg={4} md={6} sm={12}>
      <Card className="h-100 border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 certificate-card" style={{ borderRadius: '16px', overflow: 'hidden' }}>
        <Card.Header className="border-0 text-white p-4" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}>
          <div className="d-flex align-items-center">
            <FaCertificate className="me-2" />
            <span className="fw-bold">Certificate of Completion</span>
          </div>
        </Card.Header>
        <Card.Body className="d-flex flex-column p-4">
          <div className="text-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
              <FaGraduationCap size={32} className="text-white" />
            </div>
            <h5 className="card-title text-gray-900 fw-bold mb-0">{certificate.course_name}</h5>
          </div>
          
          <div className="flex-grow-1 mb-4">
            <div className="mb-3 p-2 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg">
              <strong className="text-gray-700">Learner:</strong> 
              <span className="text-gray-900 ms-2">{certificate.learner_name}</span>
            </div>
            
            <div className="mb-3 d-flex align-items-center text-sm">
              <FaCalendarAlt className="me-2 text-purple-600" />
              <span className="text-gray-700">Completed: {formatDate(certificate.completion_date)}</span>
            </div>
            
            {certificate.score_percentage && (
              <div className="mb-3 d-flex align-items-center flex-wrap gap-2">
                <FaTrophy className="text-amber-500" />
                <span className="text-gray-700">Score: {certificate.score_percentage}%</span>
                <Badge 
                  className={`px-3 py-1 rounded-full font-semibold ${
                    certificate.score_percentage >= 80 
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white' 
                      : certificate.score_percentage >= 60 
                        ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-white'
                        : 'bg-gradient-to-r from-red-500 to-rose-600 text-white'
                  }`}
                >
                  {certificate.score_percentage >= 80 ? 'Excellent' : certificate.score_percentage >= 60 ? 'Good' : 'Pass'}
                </Badge>
              </div>
            )}
            
            <div className="mb-3 text-sm">
              <small className="text-gray-600">
                Generated: {formatDate(certificate.generated_at)}
              </small>
            </div>
            
            {certificate.download_count > 0 && (
              <div className="mb-3">
                <Badge className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-3 py-1 rounded-full">
                  Downloaded {certificate.download_count} time{certificate.download_count !== 1 ? 's' : ''}
                </Badge>
              </div>
            )}
          </div>
          
          <div className="mt-auto">
            <Button
            bsPrefix="custom-hamburger"
              className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              
              onClick={() => handleDownload(certificate)}
              disabled={downloading[certificate.id]}
            >
              {downloading[certificate.id] ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Downloading...
                </>
              ) : (
                <>
                  <FaDownload className="me-2" />
                  Download Certificate
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="d-flex justify-content-center mt-6">
        <div className="d-flex align-items-center gap-2">
          <Button
            className="rounded-lg border-0 shadow-sm hover:shadow-md transition-all duration-200 font-semibold"
            style={{ 
              backgroundColor: currentPage === 1 ? '#e5e7eb' : 'white',
              color: currentPage === 1 ? '#9ca3af' : '#6366f1',
              fontWeight: '600'
            }}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </Button>
          
          <span className="mx-3 text-gray-700 font-semibold">
            Page {currentPage} of {totalPages}
          </span>
          
          <Button
            className="rounded-lg border-0 shadow-sm hover:shadow-md transition-all duration-200 font-semibold"
            style={{ 
              backgroundColor: currentPage === totalPages ? '#e5e7eb' : 'white',
              color: currentPage === totalPages ? '#9ca3af' : '#6366f1',
              fontWeight: '600'
            }}
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading your certificates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-5 py-4 sm:p-6 border border-purple-100 shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl shadow-md">
                <FaCertificate />
              </div>
              <div>
                <h2 className="text-left text-2xl sm:text-2xl font-bold text-gray-900 mb-1">My Certificates</h2>
                <p className="text-gray-600 text-sm sm:text-base m-0">
                  Your earned certificates from completed courses
                </p>
              </div>
            </div>
            <Badge bsPrefix="custom-hamburger" className="text-white bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 font-semibold rounded-lg sm:rounded-xl shadow-md transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 px-3">
              
              {totalCertificates} Certificate{totalCertificates !== 1 ? 's' : ''} Earned
            </Badge>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6">
        {certificates.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FaCertificate size={32} className="text-white" />
            </div>
            <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">No Certificates Yet</h4>
            <p className="text-gray-600 text-base sm:text-lg">
              Complete courses to earn certificates. Your achievements will appear here once you finish your learning journey!
            </p>
          </div>
        ) : (
          <>
            <Row className="g-4">
              {certificates.map(renderCertificateCard)}
            </Row>
            {renderPagination()}
          </>
        )}
      </div>
    </div>
  );
};

export default MyCertificatesPage;
